/**
 * Protective-function extractors — project rows from
 * `PLANT_PROTECTIVE_FUNCTIONS_CYPHER`,
 * `PLANT_PROTECTIVE_FUNCTION_DETAIL_CYPHER`, and
 * `PLANT_EQUIPMENT_PROTECTIVE_FUNCTIONS_CYPHER` into the typed Safety-tab
 * shapes. Pure functions, no RTK / no Neo4j driver.
 */

import { extractNode, extractNodeList, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import type {
  CypherReadResult,
  PlantEquipment,
  PlantEquipmentProtectiveFunctionLink,
  PlantLine,
  PlantLogicSolver,
  PlantProtectiveFunction,
  PlantProtectiveFunctionDetail,
  PlantProtectiveFunctionDevice,
  PlantProtectiveFunctionIndex,
  PlantProtectiveFunctionLinkRole,
  PlantProtectiveFunctionSolverGroup,
  PlantFunctionLogicDiagram,
  PlantProtectiveFunctionSummary,
  PlantSafetyPoint,
  PlantSrsFunctionGroup,
  PlantSrsFunctionGroupIndex,
  PlantSrsSection,
  PlantSite
} from '../types'

/** Display labels for `ProtectiveFunction.function_type`. */
export const PROTECTIVE_FUNCTION_TYPE_LABELS: Readonly<Record<string, string>> = {
  sif: 'SIF',
  ipf: 'IPF',
  alarm_ipl: 'Alarm IPL',
  fire_gas: 'Fire & gas',
  bpcs_ipl: 'BPCS IPL',
  prd: 'PRD',
  nrv: 'NRV',
  ros: 'ROS',
  other: 'Other'
}

/** Human label for a function_type enum, or the raw value if unknown. */
export function protectiveFunctionTypeLabel(value: string | undefined): string | undefined {
  if (!value) return undefined
  return PROTECTIVE_FUNCTION_TYPE_LABELS[value] ?? value
}

export type SilRequirementStatus = 'met' | 'not_met' | 'unavailable'

/** Parse canonical and common source forms such as `1`, `SIL1`, and `SIL-1`. */
export function parseSilLevel(value: string | undefined): number | undefined {
  if (!value) return undefined
  const match = value.trim().match(/^(?:sil\s*[-:]?\s*)?([0-4])$/i)
  return match ? Number(match[1]) : undefined
}

/** Compare the verified whole-function SIL against its required SIL. */
export function silRequirementStatus(
  requiredSil: string | undefined,
  achievedSil: string | undefined
): SilRequirementStatus {
  const required = parseSilLevel(requiredSil)
  const achieved = parseSilLevel(achievedSil)
  if (required == null || achieved == null) return 'unavailable'
  return achieved >= required ? 'met' : 'not_met'
}

/** Avoid UI labels such as `SIL SIL-2` while preserving unknown source values. */
export function formatSilLevel(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined
  const level = parseSilLevel(value)
  return level == null ? value.trim() : `SIL ${level}`
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

/** Project a landing-query row into a {@link PlantProtectiveFunctionSummary}. */
export function mapPlantProtectiveFunctionSummaryRow(
  row: Record<string, unknown>
): PlantProtectiveFunctionSummary | null {
  const fn = extractNode<PlantProtectiveFunction>(row['function'])
  if (!fn) return null
  const line = extractNode<PlantLine>(row['line']) ?? undefined
  const site = extractNode<PlantSite>(row['site']) ?? undefined
  const solver = extractNode<PlantLogicSolver>(row['solver']) ?? undefined
  const section = extractNode<PlantSrsSection>(row['section']) ?? undefined
  return {
    ...fn,
    ...(line ? { line } : {}),
    ...(site ? { site } : {}),
    ...(solver ? { solver } : {}),
    ...(section ? { section } : {}),
    sensorCount: toIntCount(row['sensorCount']),
    finalElementCount: toIntCount(row['finalElementCount']),
    implementedDiagrams: extractNodeList<PlantFunctionLogicDiagram>(
      row['implementedDiagrams']
    ).sort(compareByNameThenId)
  }
}

function deviceKey(device: PlantProtectiveFunctionDevice): string {
  return `${device.equipment.id}|${device.group ?? ''}`
}

function extractDevices(value: unknown): PlantProtectiveFunctionDevice[] {
  if (!Array.isArray(value)) return []
  const out: PlantProtectiveFunctionDevice[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const equipment = extractNode<PlantEquipment>(row['equipment'])
    if (!equipment) continue
    const device: PlantProtectiveFunctionDevice = {
      equipment,
      ...(optionalString(row['group']) ? { group: optionalString(row['group']) } : {}),
      ...(optionalString(row['voting']) ? { voting: optionalString(row['voting']) } : {}),
      ...(optionalString(row['voting_type'])
        ? { voting_type: optionalString(row['voting_type']) }
        : {}),
      ...(optionalString(row['fail_direction'])
        ? { fail_direction: optionalString(row['fail_direction']) }
        : {}),
      ...(optionalString(row['trip_setpoint'])
        ? { trip_setpoint: optionalString(row['trip_setpoint']) }
        : {}),
      ...(optionalString(row['range']) ? { range: optionalString(row['range']) } : {}),
      ...(optionalString(row['sensor_type'])
        ? { sensor_type: optionalString(row['sensor_type']) }
        : {}),
      ...(optionalString(row['action']) ? { action: optionalString(row['action']) } : {}),
      ...(optionalString(row['stroke']) ? { stroke: optionalString(row['stroke']) } : {}),
      ...(optionalString(row['fail_design'])
        ? { fail_design: optionalString(row['fail_design']) }
        : {}),
      ...(optionalString(row['test_interval'])
        ? { test_interval: optionalString(row['test_interval']) }
        : {})
    }
    const key = deviceKey(device)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(device)
  }
  out.sort((a, b) => compareByNameThenId(a.equipment, b.equipment))
  return out
}

/** Project the detail-query result into a {@link PlantProtectiveFunctionDetail}. */
export function mapPlantProtectiveFunctionDetail(
  raw: CypherReadResult,
  functionId: string
): PlantProtectiveFunctionDetail {
  const row = raw.results[0] ?? {}
  const fn = extractNode<PlantProtectiveFunction>(row['function']) ?? {
    id: functionId,
    _id: functionId,
    name: functionId
  }
  const line = extractNode<PlantLine>(row['line']) ?? undefined
  const site = extractNode<PlantSite>(row['site']) ?? undefined
  const solver: PlantLogicSolver | undefined =
    extractNode<PlantLogicSolver>(row['solver']) ?? undefined
  const section = extractNode<PlantSrsSection>(row['section']) ?? undefined
  return {
    function: fn,
    ...(line ? { line } : {}),
    ...(site ? { site } : {}),
    ...(solver ? { solver } : {}),
    ...(section ? { section } : {}),
    sensors: extractDevices(row['sensorRows']),
    finalElements: extractDevices(row['finalElementRows']),
    protectedEquipment: extractNodeList<PlantEquipment>(row['protectedEquipment']).sort(
      compareByNameThenId
    ),
    implementedDiagrams: extractNodeList<PlantFunctionLogicDiagram>(
      row['implementedDiagrams']
    ).sort(compareByNameThenId),
    implementedPoints: extractNodeList<PlantSafetyPoint>(row['implementedPoints']).sort(
      compareByNameThenId
    )
  }
}

/**
 * Build the compact Safety landing around SRS function groups rather than
 * logic solvers. A group is scoped to its Site and may span several
 * `SrsSection` chapters/categories that share one `function_group`.
 */
export function groupProtectiveFunctionsBySrsGroup(
  summaries: PlantProtectiveFunctionSummary[]
): PlantSrsFunctionGroupIndex {
  const groupsByScopeAndName = new Map<string, PlantSrsFunctionGroup>()
  const ungrouped: PlantProtectiveFunctionSummary[] = []

  for (const summary of summaries) {
    const section = summary.section
    const groupName = section?.function_group?.trim()
    if (!section || !groupName) {
      ungrouped.push(summary)
      continue
    }

    const scope = summary.site?.id ?? `package:${section.package_id ?? ''}`
    const key = `${scope}\0${groupName.toLocaleLowerCase()}`
    let group = groupsByScopeAndName.get(key)
    if (!group) {
      group = {
        id: section.id,
        name: groupName,
        ...(summary.site ? { site: summary.site } : {}),
        sections: [],
        functions: []
      }
      groupsByScopeAndName.set(key, group)
    }
    if (!group.sections.some((candidate) => candidate.id === section.id)) {
      group.sections.push(section)
    }
    group.functions.push(summary)
  }

  const groups = Array.from(groupsByScopeAndName.values())
  for (const group of groups) {
    group.sections.sort(compareByNameThenId)
    group.id = group.sections.map((section) => section.id).sort()[0] ?? group.id
    group.functions.sort(compareByNameThenId)
  }
  groups.sort((a, b) => {
    const siteOrder = compareByNameThenId(
      a.site ?? { id: '', name: '' },
      b.site ?? { id: '', name: '' }
    )
    return siteOrder || compareByNameThenId(a, b)
  })
  ungrouped.sort(compareByNameThenId)
  return { groups, ungrouped }
}

/** Keep a whole compact group when its metadata or any contained SIF matches. */
export function filterSrsFunctionGroups(
  index: PlantSrsFunctionGroupIndex,
  query: string
): PlantSrsFunctionGroupIndex {
  const needle = query.trim().toLowerCase()
  if (!needle) return index

  const groups = index.groups.filter((group) => {
    const metadata = [
      group.name,
      group.site?.name,
      ...group.sections.flatMap((section) => [
        section.name,
        section.header,
        section.function_category,
        section.package_title
      ])
    ]
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
      .join('\n')
      .toLowerCase()
    return (
      metadata.includes(needle) ||
      group.functions.some((summary) => matchesProtectiveFunctionSearch(summary, needle))
    )
  })

  return {
    groups,
    ungrouped: index.ungrouped.filter((summary) => matchesProtectiveFunctionSearch(summary, needle))
  }
}

/** Resolve any member section id to its site-scoped function group. */
export function findSrsFunctionGroupBySectionId(
  index: PlantSrsFunctionGroupIndex,
  sectionId: string
): PlantSrsFunctionGroup | undefined {
  return index.groups.find((group) => group.sections.some((section) => section.id === sectionId))
}

/**
 * Group landing summaries by logic solver. Functions with no
 * `[:SOLVED_BY]` edge (PRD/NRV/ROS and similar) fall into `unsolved`.
 */
export function groupProtectiveFunctionSummaries(
  summaries: PlantProtectiveFunctionSummary[]
): PlantProtectiveFunctionIndex {
  const groupsBySolverId = new Map<string, PlantProtectiveFunctionSolverGroup>()
  const unsolved: PlantProtectiveFunctionSummary[] = []

  for (const summary of summaries) {
    if (!summary.solver) {
      unsolved.push(summary)
      continue
    }
    let group = groupsBySolverId.get(summary.solver.id)
    if (!group) {
      group = { solver: summary.solver, functions: [], diagrams: [] }
      groupsBySolverId.set(summary.solver.id, group)
    }
    group.functions.push(summary)
  }

  const solvers = Array.from(groupsBySolverId.values()).sort((a, b) =>
    compareByNameThenId(a.solver, b.solver)
  )
  for (const group of solvers) {
    group.functions.sort(compareByNameThenId)
  }
  unsolved.sort(compareByNameThenId)
  return { solvers, unsolved, unsolvedDiagrams: [] }
}

/** True when a landing row matches a free-text Safety-tab search. */
export function matchesProtectiveFunctionSearch(
  summary: PlantProtectiveFunctionSummary,
  query: string
): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const typeLabel = protectiveFunctionTypeLabel(summary.function_type)?.toLowerCase() ?? ''
  const haystack = [
    summary.name,
    summary.id,
    summary.function_type,
    typeLabel,
    summary.sil,
    summary.required_sil,
    summary.demand_mode,
    summary.description,
    summary.line?.name,
    summary.site?.name,
    summary.solver?.name,
    summary.solver?.type,
    summary.solver?.vendor,
    summary.solver?.application,
    summary.section?.name,
    summary.section?.header,
    summary.section?.function_group,
    summary.section?.function_category,
    summary.section?.package_title,
    ...(summary.implementedDiagrams ?? []).flatMap((diagram) => [
      diagram.name,
      diagram.fld_number,
      diagram.id
    ])
  ]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join('\n')
    .toLowerCase()
  return haystack.includes(needle)
}

const REL_TYPE_TO_ROLE: Readonly<Record<string, PlantProtectiveFunctionLinkRole>> = {
  SENSED_BY: 'sensor',
  ACTUATES: 'final_element',
  PROTECTS: 'protected'
}

function mapProtectiveFunctionLinkRoles(value: unknown): PlantProtectiveFunctionLinkRole[] {
  if (!Array.isArray(value)) return []
  const roles: PlantProtectiveFunctionLinkRole[] = []
  const seen = new Set<PlantProtectiveFunctionLinkRole>()
  for (const item of value) {
    if (typeof item !== 'string') continue
    const role = REL_TYPE_TO_ROLE[item]
    if (!role || seen.has(role)) continue
    seen.add(role)
    roles.push(role)
  }
  return roles
}

/**
 * Project the equipment→protective-function reverse lookup into typed
 * links. Duplicate function ids (shouldn't happen given the cypher
 * grouping) keep the first row.
 */
export function mapPlantEquipmentProtectiveFunctions(
  raw: CypherReadResult
): PlantEquipmentProtectiveFunctionLink[] {
  const out: PlantEquipmentProtectiveFunctionLink[] = []
  const seen = new Set<string>()
  for (const row of raw.results) {
    const fn = extractNode<PlantProtectiveFunction>(row['function'])
    if (!fn || seen.has(fn.id)) continue
    seen.add(fn.id)
    out.push({ function: fn, roles: mapProtectiveFunctionLinkRoles(row['roles']) })
  }
  out.sort((a, b) => compareByNameThenId(a.function, b.function))
  return out
}
